import React, { useState } from 'react'
import { Collapse } from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Box, Flex, Text } from '@fundamentals'
import { Button, LinearProgress } from '@common/material'
import { localizedStrings } from '@core/strings'
import { useModal } from '@common/GlobalModal'
import {
  useGetRegisterItemArchivedAttachments,
  useGetRegisterItemAttachments,
} from '@core/react-query/features/registers/registers/register/items/item'
import { AddAttachmentsToRegisterItemModal } from '@modules/registers/modals/AddAttachmentsToRegisterItemModal'
import { RegisterItemAttachmentRow } from './RegisterItemAttachmentRow'
import { TransitionGroup } from 'react-transition-group'

type RegisterItemAttachmentsTabProps = {
  registerId: number
  registerItemId: number
  organisationId: number
  projectId?: number
  readOnly?: boolean
}

export const RegisterItemAttachmentsTab: React.FC<
  RegisterItemAttachmentsTabProps
> = ({ registerId, registerItemId, organisationId, projectId, readOnly }) => {
  const { showModal } = useModal()
  const { data: attachmentsResult, isInitialLoading } =
    useGetRegisterItemAttachments({
      registerId,
      registerItemId,
    })
  const { data: archivedAttachments } = useGetRegisterItemArchivedAttachments({
    registerId,
    registerItemId,
    size: 100,
  })

  const [archivedSectionExpanded, setArchivedSectionExpanded] = useState(false)
  const propertyAttachments = attachmentsResult?.propertiesAttachments ?? []
  const totalCount = attachmentsResult?.getTotalAttachmentCount() ?? 0

  const handleAddAttachment = () => {
    showModal(
      <AddAttachmentsToRegisterItemModal
        registerId={registerId}
        registerItemId={registerItemId}
        organisationId={organisationId}
        projectId={projectId}
        attachmentProperties={propertyAttachments.map((p) => ({
          propertyId: p.propertyId,
          propertyName: p.propertyName,
        }))}
      />,
    )
  }

  if (isInitialLoading) {
    return <LinearProgress />
  }

  return (
    <Box sx={{ py: 2 }}>
      <Flex justifyContent='space-between' alignItems='center' mb={2}>
        <Text variant='body2'>
          {localizedStrings.attachmentsCount(totalCount)}
        </Text>

        {!readOnly && (
          <Button variant='outlined' onClick={handleAddAttachment}>
            {localizedStrings.addAttachment}
          </Button>
        )}
      </Flex>

      {propertyAttachments.map((property) => {
        const attachments = property.attachments ?? []

        return (
          <Box key={property.propertyId} mb={2}>
            <Text variant='subtitle2' mb={0.5} sx={{ opacity: 0.83 }}>
              {property.propertyName}
            </Text>
            <TransitionGroup>
              {!!attachments.length &&
                attachments.map((attachment) => (
                  <Collapse key={attachment.id}>
                    <RegisterItemAttachmentRow
                      attachment={attachment}
                      attachments={attachments}
                      readOnly={readOnly}
                      registerId={registerId}
                      registerItemId={registerItemId}
                      organisationId={organisationId}
                      projectId={projectId}
                    />
                  </Collapse>
                ))}
            </TransitionGroup>
            {!attachments.length && (
              <Text variant='body2' color='text.secondary'>
                -
              </Text>
            )}
          </Box>
        )
      })}

      {!!archivedAttachments?.length && (
        <Flex flexDirection='column' gap={1.5} mt={4}>
          <Flex
            alignItems='center'
            justifyContent='space-between'
            onClick={() => setArchivedSectionExpanded((e) => !e)}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Text variant='subtitle2' sx={{ opacity: 0.83 }}>
              {localizedStrings.archived}
            </Text>
            <ExpandMore
              sx={{
                color: 'text.secondary',
                transform: !archivedSectionExpanded
                  ? 'rotate(-90deg)'
                  : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </Flex>

          <Collapse in={archivedSectionExpanded}>
            <TransitionGroup>
              {archivedAttachments.map((attachment) => (
                <Collapse key={attachment.id}>
                  <RegisterItemAttachmentRow
                    attachment={attachment}
                    attachments={archivedAttachments}
                    readOnly={readOnly}
                    isArchived
                    registerId={registerId}
                    registerItemId={registerItemId}
                    organisationId={organisationId}
                    projectId={projectId}
                  />
                </Collapse>
              ))}
            </TransitionGroup>
          </Collapse>
        </Flex>
      )}
    </Box>
  )
}
